import React, { useState, useEffect, useRef } from 'react';
import './InputContainer.css';
import { api } from '../api/robyBackend';
import { estimateDocumentTokens, getFileKey } from '../utils/documentTokenEstimate';

export const InputContainer = ({
  message,
  setMessage,
  handleSendMessage,
  recording,
  startRecording,
  stopRecording,
  pauseRecording,
  resumeRecording,
  showPlaceholder,
  divCount,
  animationRef,
  paused,
  handleNewSessionApp,
  app,
  setApp,
  session,
  handleFileUpload,
  toggleSidebar,
  isVisible,
  isProcessing,
  processingDocument,
  processingSuccess,
  setProcessingDocument,
  setProcessingMessage,
  refreshCurrentSession,
  // Speech-to-Text Props
  startSpeechToText,
  stopSpeechToText,
  isSpeechToTextMode,
  isTranscribing,
  speechTranscriptionFailed,
  retrySpeechToText,
  onDismissSpeechError,
  audioUploadFailed,
  retryAudioUpload,
  onDismissAudioError,
}) => {
  const [buttonContent, setButtonContent] = useState(null);
  const [applicationName, setApplicationName] = useState('');
  const [isUploadPopupVisible, setIsUploadPopupVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadType, setUploadType] = useState('audio'); // 'audio' oder 'document'
  const [isUploading, setIsUploading] = useState(false);
  const [chatUploadEnabled, setChatUploadEnabled] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null); // Context-Budget-Status (used/remaining Tokens)
  // Token-Schätzungen der ausgewählten Dateien, pro Datei-Schlüssel gecacht. Bei PDFs wird
  // dafür die Seitenzahl aus der Datei gelesen - das passiert asynchron, deshalb State.
  const [fileTokenEstimates, setFileTokenEstimates] = useState({});
  const [voiceRecordingEnabled, setVoiceRecordingEnabled] = useState(false);
  const [speechToTextEnabled, setSpeechToTextEnabled] = useState(false);
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  const [webSearchActive, setWebSearchActive] = useState(false);
  const [legalSearchEnabled, setLegalSearchEnabled] = useState(false);
  const [legalSearchActive, setLegalSearchActive] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplatePopup, setShowTemplatePopup] = useState(false);
  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(0);
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');
  const [templateFilter, setTemplateFilter] = useState('all'); // 'all', 'own', 'public'

  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const composerRef = useRef(null);
  const cancelRef = useRef({ cancelled: false });

  const isMessageEmpty = (message || '').trim() === '';

  // Berechne ob Input blockiert sein soll
  // Input ist nur blockiert wenn isProcessing, wenn Dokument verarbeitet wird (aber NICHT wenn Success angezeigt wird),
  // oder wenn Speech-to-Text transkribiert wird
  const isInputBlocked = isProcessing || (processingDocument && !processingSuccess) || isTranscribing;

  /* --- Composer-Höhe messen -> --composer-h für Bottom-Padding --- */
  useEffect(() => {
    if (!composerRef.current || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => {
      const h = Math.round(entry.contentRect.height);
      document.documentElement.style.setProperty('--composer-h', `${h}px`);
      window.dispatchEvent(new Event('composer-resize'));
    });
    ro.observe(composerRef.current);
    return () => ro.disconnect();
  }, []);

  const MIN_HEIGHT = 70; // Feste Mindesthöhe in px

  /* --- Textarea Auto-Grow (ab Mindesthöhe bis max 200px) --- */
  const applyAutoGrow = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const newHeight = Math.max(MIN_HEIGHT, Math.min(ta.scrollHeight, 200));
    ta.style.height = `${newHeight}px`;
    window.dispatchEvent(new Event('composer-resize'));
  };

  /* --- Textarea auf ursprüngliche Größe zurücksetzen --- */
  const resetTextareaHeight = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = `${MIN_HEIGHT}px`;
    window.dispatchEvent(new Event('composer-resize'));
  };

  useEffect(() => {
    applyAutoGrow();
  }, []);

  // Templates laden
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await api.get('/database/templates');
        setTemplates(response.data || []);
      } catch (error) {
        console.error('Error fetching templates:', error);
      }
    };
    fetchTemplates();
  }, []);

  // Gefilterte und sortierte Templates basierend auf Suche und Filter
  const filteredTemplates = templates
    .filter(template => {
      // Filter nach Besitzer
      if (templateFilter === 'own' && !template.is_own) return false;
      if (templateFilter === 'public' && template.is_own) return false;

      // Textsuche
      if (!templateSearchQuery.trim()) return true;
      const query = templateSearchQuery.toLowerCase();
      return (
        template.title?.toLowerCase().includes(query) ||
        template.prompt?.toLowerCase().includes(query) ||
        template.creator_firstname?.toLowerCase().includes(query) ||
        template.creator_lastname?.toLowerCase().includes(query)
      );
    })
    // Nach Nutzungsanzahl sortieren (meistgenutzte zuerst)
    .sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0));

  // Keyboard Navigation für Template-Karussell
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!showTemplatePopup || filteredTemplates.length === 0) return;

      // Ignoriere Keyboard-Navigation wenn im Suchfeld
      if (event.target.classList.contains('template-search-input')) {
        if (event.key === 'Escape') {
          setShowTemplatePopup(false);
          setTemplateSearchQuery('');
        }
        return;
      }

      if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        event.preventDefault();
        setSelectedTemplateIndex(prev =>
          prev <= 0 ? filteredTemplates.length - 1 : prev - 1
        );
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        event.preventDefault();
        setSelectedTemplateIndex(prev =>
          prev >= filteredTemplates.length - 1 ? 0 : prev + 1
        );
      } else if (event.key === 'Escape') {
        setShowTemplatePopup(false);
        setTemplateSearchQuery('');
      }
    };

    if (showTemplatePopup) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showTemplatePopup, selectedTemplateIndex, filteredTemplates]);

  // Reset selected index wenn sich gefilterte Liste ändert
  useEffect(() => {
    if (selectedTemplateIndex >= filteredTemplates.length) {
      setSelectedTemplateIndex(Math.max(0, filteredTemplates.length - 1));
    }
  }, [filteredTemplates.length, selectedTemplateIndex]);

  // Template auswählen
  const handleTemplateSelect = async (template) => {
    setMessage(template.prompt || '');
    setShowTemplatePopup(false);
    // Textarea fokussieren und Auto-Grow auslösen
    if (textareaRef.current) {
      textareaRef.current.focus();
      setTimeout(applyAutoGrow, 0);
    }
    // Nutzungszähler erhöhen
    try {
      await api.post(`/database/templates/${template._id}/use`);
      // Lokalen State aktualisieren, damit die Anzeige sofort korrekt ist
      setTemplates(prevTemplates =>
        prevTemplates.map(t =>
          t._id === template._id
            ? { ...t, usage_count: (t.usage_count || 0) + 1 }
            : t
        )
      );
    } catch (error) {
      console.error('Error incrementing template usage:', error);
    }
  };

  // Template Popup öffnen
  const openTemplatePopup = () => {
    setSelectedTemplateIndex(0);
    setTemplateSearchQuery('');
    setTemplateFilter('all');
    setShowTemplatePopup(true);
  };

  // Token-Schätzung pro ausgewählter Datei. Bei PDFs wird die Seitenzahl aus der Datei
  // gelesen und mit den Tokens pro Seite verrechnet (Details in documentTokenEstimate.js) -
  // die Dateigröße allein ist bei PDFs kein brauchbarer Maßstab. Das Ergebnis liegt im
  // Cache-State, hier wird nur nachgeschlagen.
  // Der Verarbeitungsmodus steckt im Cache-Schlüssel: eine Vision-Seite kostet ein
  // Vielfaches einer Textseite, die Schätzung gilt also immer nur für einen Modus.
  const visionMode = !!uploadStatus?.force_ai_vision;
  const estimateCacheKey = (file) => `${getFileKey(file)}|${visionMode ? 'vision' : 'text'}`;
  const getFileEstimate = (file) => fileTokenEstimates[estimateCacheKey(file)] || null;

  // Auslastung in % runden (0-100, auf Wunsch über 100 für sichtbaren Overflow)
  const toPercent = (ratio) => Math.round((ratio || 0) * 100);

  // Datum formatieren
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Ersteller-Anzeige formatieren
  const formatTemplateCreator = (template) => {
    const firstname = template.creator_firstname || '';
    const lastname = template.creator_lastname || '';
    if (firstname && lastname) {
      return `${firstname} ${lastname.charAt(0)}.`;
    }
    return template.created_by || '';
  };

  /* --- Textarea zurücksetzen wenn message leer wird --- */
  useEffect(() => {
    if (message === '') {
      resetTextareaHeight();
    }
  }, [message]);

  /* --- Buttons laden --- */
  useEffect(() => {
    let alive = true;
    cancelRef.current.cancelled = false;

    const defaultButtons = () => (
      <div className="new-chat-button-container">
        <button
          className="new-chat-button"
          onClick={() => handleAppClick(session, applicationName)}
          aria-label="Open app"
        >
          <img src={process.env.PUBLIC_URL + '/add_white.png'} alt="" className="button-icon" />
        </button>
      </div>
    );

    const fetchButtonContent = async () => {
      try {
        if (!app || !session?._id) return;

        const responseApp = await api.get(`/database/apps/${app}`, { validateStatus: s => s < 500 });
        if (responseApp.status >= 400 || !responseApp.data) {
          if (!alive) return;
          setApplicationName('');
          setChatUploadEnabled(false);
          setVoiceRecordingEnabled(false);
          setSpeechToTextEnabled(false);
          setWebSearchEnabled(false);
          setWebSearchActive(false);
          setLegalSearchEnabled(false);
          setLegalSearchActive(false);
          setButtonContent(defaultButtons());
          return;
        }

        const application = responseApp.data.application ?? '';
        const partnerId = responseApp.data.partner_id ?? null;
        const chatUpload = responseApp.data.chat_upload ?? false;
        const voiceRecording = responseApp.data.voice_recording ?? false;
        const speechToText = responseApp.data.speech_to_text_enabled ?? false;
        const webSearch = responseApp.data.web_search_enabled ?? false;
        const legalSearch = responseApp.data.legal_search_enabled ?? false;

        if (!alive) return;

        setApplicationName(application);
        setChatUploadEnabled(chatUpload);
        setVoiceRecordingEnabled(voiceRecording);
        setSpeechToTextEnabled(speechToText);
        setWebSearchEnabled(webSearch);
        if (!webSearch) setWebSearchActive(false);
        setLegalSearchEnabled(legalSearch);
        if (!legalSearch) setLegalSearchActive(false);

        let partnerApp = null;
        if (partnerId) {
          const partnerResponse = await api.get(`/database/partner_apps/${app}/${partnerId}`, { validateStatus: s => s < 500 });
          if (partnerResponse.status < 400 && partnerResponse.data) partnerApp = partnerResponse.data;
        }

        const partnerAppIcon =
          partnerApp?.application === 'Assistent'
            ? process.env.PUBLIC_URL + '/search.png'
            : process.env.PUBLIC_URL + '/edit_white.png';

        const buttons = (
          <div className="new-chat-button-container">
            {partnerApp?.application && (
              <button
                className="new-chat-button"
                onClick={() => handleAppClick(session, partnerApp.application)}
                aria-label="Open partner app"
              >
                <img src={partnerAppIcon} alt="" className="button-icon" />
              </button>
            )}
            <button
              className="new-chat-button"
              onClick={() => handleAppClick(session, application)}
              aria-label="Open main app"
            >
              <img src={process.env.PUBLIC_URL + '/add_white.png'} alt="" className="button-icon" />
            </button>
          </div>
        );

        setButtonContent(buttons);
      } catch (error) {
        // Ignoriere abgebrochene Requests (normal beim Session-Wechsel)
        if (error.name === 'CanceledError' || error.name === 'AbortError') {
          return;
        }
        console.error('Error loading buttons (soft-handled):', error);

        // WICHTIG: Bei Fehler NICHT die voice_recording und chat_upload States zurücksetzen!
        // Diese wurden bereits korrekt gesetzt (Zeile 125-126), nur die Partner-App ist fehlgeschlagen
        // setChatUploadEnabled(false); // ENTFERNT - States bleiben erhalten
        // setVoiceRecordingEnabled(false); // ENTFERNT - States bleiben erhalten
        setButtonContent(defaultButtons());
      }
    };

    fetchButtonContent();
    return () => { alive = false; cancelRef.current.cancelled = true; };
  }, [app, session?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  /* --- Context-Budget-Status laden (wie viele Tokens durch Dokumente belegt sind) --- */
  const fetchUploadStatus = async () => {
    if (!app || !session?._id) return;
    try {
      const response = await api.get(`/chat/ai_foundry_agent/upload_status/${app}/${session._id}`, { validateStatus: s => s < 500 });
      if (response.status < 400 && response.data) {
        setUploadStatus(response.data);
      }
    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') return;
      console.error('Error loading upload status:', error);
    }
  };

  useEffect(() => {
    if (chatUploadEnabled && app && session?._id) {
      fetchUploadStatus();
    } else {
      setUploadStatus(null);
    }
  }, [chatUploadEnabled, app, session?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sobald die Hintergrund-Verarbeitung eines Uploads abgeschlossen ist (processingDocument: true -> false),
  // Budget-Status neu laden, da die Dokumente jetzt tatsächlich in der Session gezählt werden.
  const prevProcessingDocumentRef = useRef(false);
  useEffect(() => {
    if (prevProcessingDocumentRef.current && !processingDocument) {
      fetchUploadStatus();
    }
    prevProcessingDocumentRef.current = processingDocument;
  }, [processingDocument]); // eslint-disable-line react-hooks/exhaustive-deps

  /* --- Token-Schätzung der ausgewählten Dateien (PDFs: Seitenzahl aus der Datei lesen) --- */
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const currentKeys = selectedFiles.map(estimateCacheKey);
      // Dateien, für die noch keine Schätzung vorliegt
      const pending = selectedFiles.filter(file => !fileTokenEstimates[estimateCacheKey(file)]);

      if (pending.length > 0) {
        const results = await Promise.all(
          pending.map(async (file) => {
            try {
              return [estimateCacheKey(file), await estimateDocumentTokens(file, { visionMode })];
            } catch (error) {
              console.error('Token-Schätzung fehlgeschlagen:', error);
              return [estimateCacheKey(file), { tokens: 0, pages: null, basis: 'size' }];
            }
          })
        );
        if (cancelled) return;
        setFileTokenEstimates(prev => {
          const next = { ...prev };
          results.forEach(([key, estimate]) => { next[key] = estimate; });
          // Schätzungen abgewählter Dateien verwerfen, damit der Cache nicht mitwächst
          Object.keys(next).forEach(key => { if (!currentKeys.includes(key)) delete next[key]; });
          return next;
        });
      } else if (Object.keys(fileTokenEstimates).some(key => !currentKeys.includes(key))) {
        setFileTokenEstimates(prev => {
          const next = {};
          currentKeys.forEach(key => { if (prev[key]) next[key] = prev[key]; });
          return next;
        });
      }
    };

    run();
    return () => { cancelled = true; };
  }, [selectedFiles, visionMode]); // eslint-disable-line react-hooks/exhaustive-deps

  /* --- Actions --- */
  const handleAttachFileClick = (type = 'audio') => {
    if (isVisible) toggleSidebar();
    setUploadType(type);
    setIsUploadPopupVisible(true);
    setSelectedFile(null);
    setSelectedFiles([]);
    if (type === 'document' && chatUploadEnabled) fetchUploadStatus();
  };

  const handleSendMessageAndClear = async () => {
    if (isInputBlocked) return;
    if (message.trim() === '') return;

    await handleSendMessage(undefined, false, webSearchActive, legalSearchActive);
  };

  const handleAppClick = async (sessionObj, expectedApplication) => {
    try {
      const sessionResponse = await api.get(`/database/sessions/${sessionObj._id}`);
      const fetchedSession = sessionResponse.data;
      const sessionApp = fetchedSession['app'];
      const appResponse = await api.get(`/database/apps/${sessionApp}`);
      const fetchedApp = appResponse.data;

      if (fetchedApp['application'] === expectedApplication) {
        setApp(fetchedApp._id);
        handleNewSessionApp(fetchedApp._id);
      } else {
        const partnerResponse = await api.get(`/database/partner_apps/${fetchedApp['_id']}/${fetchedApp['partner_id']}`);
        const partner_app = partnerResponse.data;
        if (partner_app && partner_app._id !== fetchedApp._id) {
          setApp(partner_app._id);
          handleNewSessionApp(partner_app._id);
        } else {
          console.error('Keine andere Partner-App gefunden.');
        }
      }
    } catch (error) {
      console.error(`Fehler in handleAppClick für ${expectedApplication}:`, error);
    }
  };

  const handleFileUploadInternal = async () => {
    if (uploadType === 'audio' && selectedFile) {
      const allowedTypes = [
        'audio/wav','audio/x-wav','audio/mp4','audio/mpga','audio/m4a',
        'audio/mp3','audio/mpeg','audio/ogg','audio/webm',
      ];
      if (!allowedTypes.includes(selectedFile.type)) {
        alert('Bitte eine gültige Audiodatei hochladen.');
        return;
      }
      handleFileUpload(selectedFile);
      setSelectedFile(null);
    } else if (uploadType === 'document' && selectedFiles.length > 0) {
      // Session-Dokumente hochladen
      await handleDocumentUpload(selectedFiles);
      setSelectedFiles([]);
    }
  };

  const handleDocumentUpload = async (files) => {
    if (!app || !session?._id) {
      alert('Keine aktive Session gefunden');
      return;
    }

    setIsUploading(true);

    // Sofort Processing-Status setzen (noch BEVOR Upload startet)
    console.log('📤 Upload startet - setze Processing-Status...');
    if (setProcessingDocument && setProcessingMessage) {
      setProcessingDocument(true);
      setProcessingMessage(`Verarbeite ${files.length} Dokument(e)`);
    }

    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });

      const response = await api.post(
        `/chat/ai_foundry_agent/upload/${app}/${session._id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 90000, // 90 Sekunden für Dokument-Uploads (läuft im Backend weiter)
        }
      );

      // Upload erfolgreich - Session neu laden um processing_document Status zu bekommen
      console.log('📤 Upload erfolgreich, lade Session neu...');
      if (refreshCurrentSession) {
        await refreshCurrentSession();
      }
      fetchUploadStatus();

      // Kein Alert mehr - Success wird durch Processing-Message angezeigt
    } catch (error) {
      // Ignoriere abgebrochene Requests (normal beim Session-Wechsel)
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        console.log('Document upload was cancelled (session change)');
        return;
      }

      console.error('Fehler beim Dokument-Upload:', error);

      // Zeige User-freundliche Fehlermeldung
      const errorDetail = error.response?.data?.detail || error.message || 'Unbekannter Fehler';
      alert('Fehler beim Hochladen der Dokumente: ' + errorDetail);

      // Bei Fehler Processing-Status GARANTIERT zurücksetzen
      if (setProcessingDocument && setProcessingMessage) {
        setProcessingDocument(false);
        setProcessingMessage(null);
      }
    } finally {
      // Garantiere, dass Upload-State IMMER zurückgesetzt wird
      setIsUploading(false);

      // Processing-Status wird durch Polling aktualisiert (bleibt true bis Backend fertig ist)
    }
  };

  const validateFilename = (filename) => {
    if (!filename || !filename.trim()) {
      return { valid: false, error: "Dateiname ist leer." };
    }

    // Entferne Leerzeichen am Anfang/Ende
    filename = filename.trim();

    // Prüfe auf unzulässige Zeichen (Dateisystem-unsichere Zeichen)
    const invalidCharsRegex = /[/\\:*?"<>|]/;
    if (invalidCharsRegex.test(filename)) {
      return { valid: false, error: `Dateiname "${filename}" enthält unzulässige Zeichen (/, \\, :, *, ?, ", <, >, |).` };
    }

    // Prüfe ob Dateiendung vorhanden ist
    if (!filename.includes('.')) {
      return { valid: false, error: `Dateiname "${filename}" muss eine Dateiendung haben.` };
    }

    // Teile Dateiname und Endung
    const lastDotIndex = filename.lastIndexOf('.');
    const nameWithoutExt = filename.substring(0, lastDotIndex);
    const extension = filename.substring(lastDotIndex + 1);

    // Prüfe auf mehrfache Punkte im Dateinamen (nur vor Endung erlaubt)
    if (nameWithoutExt.includes('.')) {
      return { valid: false, error: `Dateiname "${filename}" darf keine Punkte enthalten (nur vor der Dateiendung).` };
    }

    // Prüfe Mindestlänge des Dateinamens (ohne Endung)
    if (nameWithoutExt.length < 1) {
      return { valid: false, error: `Dateiname muss mindestens 1 Zeichen lang sein (ohne Dateiendung).` };
    }

    // Prüfe ob Endung gültig ist (mindestens 1 Zeichen)
    if (extension.length < 1) {
      return { valid: false, error: `Dateiendung von "${filename}" ist ungültig.` };
    }

    return { valid: true };
  };

  const validateDocumentFile = (file) => {
    const allowedMime = new Set([
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/plain",
      "message/rfc822",
      "application/vnd.ms-outlook",
    ]);
    const allowedExt = /\.(pdf|doc|docx|xls|xlsx|txt|eml|msg)$/i;
    const maxSize = 15 * 1024 * 1024; // 15MB in bytes

    // Dateiname validieren
    const filenameValidation = validateFilename(file.name);
    if (!filenameValidation.valid) {
      return filenameValidation;
    }

    if (!allowedMime.has(file.type) && !allowedExt.test(file.name)) {
      return { valid: false, error: "Nur PDF, Word (.doc/.docx), Excel (.xls/.xlsx), Text (.txt) und E-Mails (.eml/.msg) sind erlaubt." };
    }

    // Im Report-Modus keine PDFs erlauben
    if (applicationName === 'Report' && file.name.toLowerCase().endsWith('.pdf')) {
      return { valid: false, error: "Im Report-Modus können keine PDF-Dateien hochgeladen werden. Bitte verwenden Sie Word- oder Excel-Dateien." };
    }

    if (file.size > maxSize) {
      return { valid: false, error: `Die Datei "${file.name}" ist zu groß. Maximale Größe: 15MB.` };
    }

    return { valid: true };
  };

  /* --- Drag & Drop --- */
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      if (uploadType === 'audio') {
        setSelectedFile(files[0]);
      } else if (uploadType === 'document') {
        const fileArray = Array.from(files);
        // Validiere jede Datei
        for (const file of fileArray) {
          const validation = validateDocumentFile(file);
          if (!validation.valid) {
            alert(validation.error);
            return;
          }
        }
        setSelectedFiles(fileArray);
      }
    }
  };

  const handleMultipleFilesChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      // Validiere jede Datei
      for (const file of fileArray) {
        const validation = validateDocumentFile(file);
        if (!validation.valid) {
          alert(validation.error);
          e.target.value = ''; // Reset input
          return;
        }
      }
      // Füge neue Dateien zu bestehenden hinzu
      setSelectedFiles(prev => [...prev, ...fileArray]);
      e.target.value = ''; // Reset input für erneute Auswahl
    }
  };

  // Budget-Auswertung schon bei der Auswahl (vor dem Upload) - nicht erst danach, wenn
  // schon viele Dateien ausgewählt wurden. Kombiniert bereits genutztes Dokument-Budget
  // der Session (60% des Context Windows, Rest ist Reserve für Verlauf/Antwort) mit einer
  // Schätzung für die gerade ausgewählten (noch nicht hochgeladenen) Dateien.
  const selectedEstimatedTokens = uploadType === 'document'
    ? selectedFiles.reduce((sum, f) => sum + (getFileEstimate(f)?.tokens || 0), 0)
    : 0;
  // Solange eine Schätzung noch läuft (PDF wird gelesen), keine Überschreitung melden -
  // sonst blinkt kurzzeitig eine Warnung auf unvollständiger Basis auf.
  const estimatesPending = uploadType === 'document' && selectedFiles.some(f => !getFileEstimate(f));
  // Konnte bei mindestens einer Datei die Seitenzahl nicht gelesen werden, ist die Zahl
  // nur eine Hausnummer - das wird in der Anzeige kenntlich gemacht.
  const estimateIsRough = uploadType === 'document'
    && selectedFiles.some(f => getFileEstimate(f)?.basis === 'size');
  const combinedUsedTokens = (uploadStatus?.used_tokens || 0) + selectedEstimatedTokens;
  const combinedRatio = uploadStatus?.document_token_budget
    ? combinedUsedTokens / uploadStatus.document_token_budget
    : 0;
  const combinedOverflow = uploadStatus && !estimatesPending
    ? combinedUsedTokens > uploadStatus.document_token_budget
    : false;

  return (
    <>
      <div ref={composerRef} className="input-container">
        {/* Retry-Bar bei fehlgeschlagenem Audio-Upload */}
        {audioUploadFailed && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '8px 12px', marginBottom: '4px',
            background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px',
            fontSize: '13px', color: '#856404'
          }}>
            <span style={{ flex: 1 }}>Sprachnachricht fehlgeschlagen — Aufnahme ist noch gespeichert.</span>
            <button
              onClick={retryAudioUpload}
              disabled={isProcessing}
              style={{
                padding: '4px 10px', borderRadius: '6px', border: 'none',
                background: '#198754', color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: 600
              }}
            >
              {isProcessing ? 'Lädt...' : 'Nochmal versuchen'}
            </button>
            <button
              onClick={onDismissAudioError}
              style={{
                padding: '4px 8px', borderRadius: '6px', border: 'none',
                background: 'transparent', color: '#856404', cursor: 'pointer', fontSize: '14px', lineHeight: 1
              }}
              title="Verwerfen"
            >
              ✕
            </button>
          </div>
        )}
        {/* Retry-Bar bei fehlgeschlagener Transkription */}
        {speechTranscriptionFailed && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '8px 12px', marginBottom: '4px',
            background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px',
            fontSize: '13px', color: '#856404'
          }}>
            <span style={{ flex: 1 }}>Transkription fehlgeschlagen — Aufnahme ist noch gespeichert.</span>
            <button
              onClick={retrySpeechToText}
              disabled={isTranscribing}
              style={{
                padding: '4px 10px', borderRadius: '6px', border: 'none',
                background: '#198754', color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: 600
              }}
            >
              {isTranscribing ? 'Lädt...' : 'Nochmal versuchen'}
            </button>
            <button
              onClick={onDismissSpeechError}
              style={{
                padding: '4px 8px', borderRadius: '6px', border: 'none',
                background: 'transparent', color: '#856404', cursor: 'pointer', fontSize: '14px', lineHeight: 1
              }}
              title="Verwerfen"
            >
              ✕
            </button>
          </div>
        )}

        {/* Web-Search Toggle Button - rechts oben über der InputBox */}
        {webSearchEnabled && !recording && (
          <div className={`web-search-button-container${templates.length > 0 ? ' web-search-button-container--offset' : ''}`}>
            <button
              className={`web-search-toggle-btn${webSearchActive ? ' web-search-toggle-btn--active' : ''}`}
              onClick={() => setWebSearchActive(prev => !prev)}
              title={webSearchActive ? 'Websuche aktiv – klicken zum Deaktivieren' : 'Websuche aktivieren'}
            >
              <img src={process.env.PUBLIC_URL + '/world.png'} alt="Websuche" />
            </button>
          </div>
        )}

        {/* Bundesrechtsdatenbank Toggle Button */}
        {legalSearchEnabled && !recording && (
          <div
            className="legal-search-button-container"
            style={{ right: `${(templates.length > 0 ? 60 : 0) + (webSearchEnabled ? 60 : 0)}px` }}
          >
            <button
              className={`legal-search-toggle-btn${legalSearchActive ? ' legal-search-toggle-btn--active' : ''}`}
              onClick={() => setLegalSearchActive(prev => !prev)}
              title={legalSearchActive ? 'Rechtssuche aktiv – klicken zum Deaktivieren' : 'Bundesrechtsdatenbank aktivieren'}
            >
              ⚖
            </button>
          </div>
        )}

        {/* Template-Auswahl Button über der InputBox - grüner runder Button rechts */}
        {templates.length > 0 && !recording && (
          <div className="template-picker-button-container">
            <button
              className="template-picker-btn"
              onClick={openTemplatePopup}
              disabled={isInputBlocked}
              title="Template auswählen"
            >
              <img src={process.env.PUBLIC_URL + '/note.png'} alt="Templates" />
            </button>
          </div>
        )}

        {!recording && (
          <textarea
            ref={textareaRef}
            value={message}
            onInput={(e) => { setMessage(e.target.value); applyAutoGrow(); window.dispatchEvent(new Event('composer-input')); }}
            placeholder={showPlaceholder ? 'Stelle irgendeine Frage.' : ''}
            rows={1}
            className="input"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (!isInputBlocked) handleSendMessageAndClear();
              }
            }}
            disabled={recording}
            aria-disabled={isInputBlocked ? 'true' : 'false'}
            style={{ opacity: isInputBlocked ? 0.85 : 1 }}
          />
        )}

        {recording && (
          <div className="wave-animation" ref={animationRef}>
            {[...Array(divCount)].map((_, index) => (
              <div key={index}></div>
            ))}
          </div>
        )}

        {/* Icons rechts */}
        <div className="icons-container">
          {/* Upload und Speech-to-Text übereinander wenn beide aktiv */}
          {(chatUploadEnabled || speechToTextEnabled) && !recording && (
            <div className={chatUploadEnabled && speechToTextEnabled ? "icons-stacked" : ""}>
              {chatUploadEnabled && (
                <button
                  className="icon-button"
                  onClick={() => handleAttachFileClick('document')}
                  disabled={isInputBlocked}
                  title="Dokument hochladen"
                >
                  <img src={process.env.PUBLIC_URL + '/upload_icon.png'} alt="Upload Document" />
                </button>
              )}

              {/* Speech-to-Text Button - für Live-Transkription in die Input-Box */}
              {speechToTextEnabled && !isTranscribing && (
                <button
                  className="icon-button speech-to-text-button"
                  onClick={startSpeechToText}
                  disabled={isInputBlocked}
                  title="Spracheingabe (Text wird in die Box eingefügt)"
                >
                  <img src={process.env.PUBLIC_URL + '/mic.png'} alt="Speech to Text" />
                </button>
              )}

              {/* Speech-to-Text wird transkribiert - Lade-Indikator */}
              {speechToTextEnabled && isTranscribing && (
                <button
                  className="icon-button speech-to-text-button transcribing"
                  disabled
                  title="Transkribiere..."
                  style={{ opacity: 0.6 }}
                >
                  <span className="transcribing-spinner"></span>
                </button>
              )}
            </div>
          )}

          {/* Voice Recording Button (nur wenn voice_recording aktiviert und NICHT speech_to_text) */}
          {voiceRecordingEnabled && !speechToTextEnabled && !recording && (
            <button
              className="icon-button"
              onClick={startRecording}
              disabled={isInputBlocked}
              title="Aufnahme starten"
            >
              <img src={process.env.PUBLIC_URL + '/mic.png'} alt="Start Recording" />
            </button>
          )}

          {/* Recording läuft (Voice Recording ODER Speech-to-Text) - gleiche Buttons */}
          {recording && (
            <>
              {/* Pause/Resume Button - für beide Modi */}
              <button
                className="icon-button"
                onClick={paused ? resumeRecording : pauseRecording}
                disabled={isInputBlocked}
                title={paused ? "Fortsetzen" : "Pausieren"}
                style={{ pointerEvents: isInputBlocked ? 'none' : 'auto' }}
              >
                <img src={process.env.PUBLIC_URL + (paused ? '/resume.png' : '/pause.png')} alt={paused ? "Resume" : "Pause"} />
              </button>
              {/* Stop Button */}
              <button
                className="icon-button"
                onClick={isSpeechToTextMode ? stopSpeechToText : stopRecording}
                disabled={isInputBlocked}
                title="Aufnahme beenden"
                style={{ pointerEvents: isInputBlocked ? 'none' : 'auto' }}
              >
                <img src={process.env.PUBLIC_URL + '/stop.png'} alt="Stop" />
              </button>
            </>
          )}

          {!recording && (
            <button
              className="icon-button send-button"
              onClick={handleSendMessageAndClear}
              disabled={isInputBlocked}
              title="Nachricht senden"
              style={{ pointerEvents: isInputBlocked ? 'none' : 'auto' }}
            >
              <img src={process.env.PUBLIC_URL + '/send.png'} alt="Send" />
            </button>
          )}
        </div>

        {isUploadPopupVisible && (
          <div className={`uploadPopupOverlay ${isDragging ? 'dragging' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="uploadPopup" onClick={(e) => e.stopPropagation()}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <h2>{uploadType === 'audio' ? 'Audiodatei hochladen' : 'Dokument(e) hochladen'}</h2>

              {/* Context-Window-Anzeige: was schon belegt ist und wie viel noch möglich ist.
                  Reagiert sofort auf die aktuelle Auswahl (noch bevor hochgeladen wird), damit
                  eine Überschreitung nicht erst nach der Auswahl vieler Dateien auffällt.
                  Zeigt IMMER den Anteil am vollen Context Window - dieselbe Basis wie der
                  Ring oben neben dem App-Namen, damit beide Zahlen übereinstimmen. */}
              {uploadType === 'document' && uploadStatus && (
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                    <span>Dokument-Budget {selectedFiles.length > 0 ? '(inkl. Auswahl)' : 'genutzt'}</span>
                    <span>{estimatesPending ? 'wird berechnet …' : `${estimateIsRough ? 'ca. ' : ''}${toPercent(combinedRatio)}%`}</span>
                  </div>
                  <div style={{ width: '100%', height: '8px', borderRadius: '4px', background: '#eee', overflow: 'hidden' }}>
                    <div style={{
                      width: `${Math.min(100, toPercent(combinedRatio))}%`,
                      height: '100%',
                      background: combinedOverflow ? '#dc3545' : combinedRatio > 0.8 ? '#e6b800' : '#026c70',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                  {combinedOverflow ? (
                    <p style={{ fontSize: '12px', color: '#dc3545', marginTop: '6px', marginBottom: 0 }}>
                      ⚠️ {selectedFiles.length > 0 ? 'Diese Auswahl überschreitet' : 'Budget überschreitet bereits'} das Dokument-Budget ({toPercent(combinedRatio)}%).
                      Der Upload wird trotzdem verarbeitet, kann aber die Antwortqualität verringern.
                    </p>
                  ) : (
                    <p style={{ fontSize: '12px', color: '#888', marginTop: '6px', marginBottom: 0 }}>
                      Noch ca. {Math.max(0, 100 - toPercent(combinedRatio))}% Dokument-Budget verfügbar, bevor das Context-Fenster ausgereizt wird.
                    </p>
                  )}
                  {uploadStatus.documents && uploadStatus.documents.length > 0 && (
                    <details style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                      <summary style={{ cursor: 'pointer' }}>
                        {uploadStatus.documents.length} bereits hochgeladene Dokument(e) in dieser Session
                      </summary>
                      <ul style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
                        {uploadStatus.documents.map((doc, idx) => (
                          <li key={idx}>
                            {doc.filename} ({doc.page_count} Seiten, ~{toPercent(doc.total_tokens / uploadStatus.document_token_budget)}% des Budgets)
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}

              <div className={`uploadBox ${isDragging ? 'dragging' : ''}`} onClick={() => fileInputRef.current.click()}>
                {uploadType === 'audio' && selectedFile ? (
                  <p className="selectedFileName">{selectedFile.name}</p>
                ) : uploadType === 'document' && selectedFiles.length > 0 ? (
                  <div className="selectedFilesList">
                    {selectedFiles.map((file, idx) => {
                      // Geschätzter Anteil dieser Datei am Dokument-Budget - schon bei der
                      // Auswahl sichtbar, nicht erst nach dem Upload. Bei PDFs basiert die
                      // Zahl auf der Seitenzahl, nicht auf der Dateigröße.
                      const estimate = getFileEstimate(file);
                      const filePercent = estimate && uploadStatus?.document_token_budget
                        ? toPercent(estimate.tokens / uploadStatus.document_token_budget)
                        : null;
                      return (
                        <p key={idx} className="selectedFileName">
                          {file.name}
                          {filePercent === null ? (
                            <span style={{ marginLeft: '8px', fontSize: '11px', color: '#888' }}>…</span>
                          ) : (
                            <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: 600, color: filePercent > 50 ? '#dc3545' : '#026c70' }}>
                              {estimate.pages ? `${estimate.pages} ${estimate.pages === 1 ? 'Seite' : 'Seiten'}, ` : ''}~{filePercent}%
                            </span>
                          )}
                        </p>
                      );
                    })}
                  </div>
                ) : (
                  <>
                    <p className="uploadBoxText">
                      {uploadType === 'document' ? 'Dokument(e) hierher ziehen oder klicken' : 'Datei hierher ziehen oder klicken'}
                    </p>
                    <img src={process.env.PUBLIC_URL + '/upload_icon.png'} alt="Upload Icon" className="uploadIcon" />
                  </>
                )}
              </div>

              {/* Hinweis für PDFs im Report-Modus */}
              {uploadType === 'document' && selectedFiles.length > 0 && applicationName === 'Report' && selectedFiles.some(f => f.name.toLowerCase().endsWith('.pdf')) && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '6px',
                  fontSize: '13px',
                  lineHeight: '1.5',
                  color: '#856404'
                }}>
                  <strong>⚠️ Hinweis:</strong> Im Report-Modus können keine PDF-Dateien hochgeladen werden. Bitte verwenden Sie Word- oder Excel-Dateien.
                </div>
              )}
              <input
                type="file"
                accept={uploadType === 'audio' ? 'audio/*' : (applicationName === 'Report' ? '.doc,.docx,.xls,.xlsx,.txt,.eml,.msg' : '.pdf,.doc,.docx,.xls,.xlsx,.txt,.eml,.msg')}
                multiple={uploadType === 'document'}
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={uploadType === 'audio' ? (e) => setSelectedFile(e.target.files[0]) : handleMultipleFilesChange}
              />
              <div className="popupButtons">
                <button
                  className="confirmButton"
                  onClick={(e) => { e.stopPropagation(); handleFileUploadInternal(); setIsUploadPopupVisible(false); }}
                  disabled={
                    isUploading ||
                    (uploadType === 'audio' ? !selectedFile : selectedFiles.length === 0) ||
                    (uploadType === 'document' && applicationName === 'Report' && selectedFiles.some(f => f.name.toLowerCase().endsWith('.pdf')))
                  }
                >
                  {isUploading ? 'Lade hoch...' : 'Hochladen'}
                </button>
                <button
                  className="cancelButton"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsUploadPopupVisible(false);
                    setSelectedFile(null);
                    setSelectedFiles([]);
                  }}
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="input-helper" aria-live="polite">
        AI can make mistakes. Check important info.
      </div>

      {/* Apple-Style Template Karussell Popup */}
      {showTemplatePopup && (
        <div className="template-carousel-overlay" onClick={() => { setShowTemplatePopup(false); setTemplateSearchQuery(''); }}>
          <div className="template-carousel-popup" onClick={(e) => e.stopPropagation()}>
            <div className="template-carousel-header">
              <h3>Template auswählen</h3>
              <button
                className="template-carousel-close"
                onClick={() => { setShowTemplatePopup(false); setTemplateSearchQuery(''); }}
              >
                ×
              </button>
            </div>

            {/* Suchfeld und Filter */}
            <div className="template-search-filter-row">
              <div className="template-search-container">
                <input
                  type="text"
                  className="template-search-input"
                  placeholder="Suchen..."
                  value={templateSearchQuery}
                  onChange={(e) => {
                    setTemplateSearchQuery(e.target.value);
                    setSelectedTemplateIndex(0);
                  }}
                  autoFocus
                />
                {templateSearchQuery && (
                  <button
                    className="template-search-clear"
                    onClick={() => { setTemplateSearchQuery(''); setSelectedTemplateIndex(0); }}
                  >
                    ×
                  </button>
                )}
              </div>
              <div className="template-filter-buttons">
                <button
                  className={`template-filter-btn ${templateFilter === 'all' ? 'active' : ''}`}
                  onClick={() => { setTemplateFilter('all'); setSelectedTemplateIndex(0); }}
                >
                  Alle
                </button>
                <button
                  className={`template-filter-btn ${templateFilter === 'own' ? 'active' : ''}`}
                  onClick={() => { setTemplateFilter('own'); setSelectedTemplateIndex(0); }}
                >
                  Meine
                </button>
                <button
                  className={`template-filter-btn ${templateFilter === 'public' ? 'active' : ''}`}
                  onClick={() => { setTemplateFilter('public'); setSelectedTemplateIndex(0); }}
                >
                  Öffentlich
                </button>
              </div>
            </div>

            {filteredTemplates.length === 0 ? (
              <div className="template-no-results">
                Keine Templates gefunden
              </div>
            ) : (
              <>
                <div className="template-carousel-container">
                  <div className="template-carousel-wheel">
                    {filteredTemplates.map((template, index) => {
                      const offset = index - selectedTemplateIndex;
                      const isActive = index === selectedTemplateIndex;
                      const absOffset = Math.abs(offset);

                      return (
                        <div
                          key={template._id}
                          className={`template-carousel-item ${isActive ? 'active' : ''}`}
                          style={{
                            '--offset': offset,
                            transform: `
                              translateY(${offset * 75}px)
                              translateZ(${isActive ? 50 : -absOffset * 30}px)
                              scale(${isActive ? 1.05 : 1 - absOffset * 0.12})
                              rotateX(${offset * -12}deg)
                            `,
                            opacity: absOffset > 2 ? 0 : isActive ? 1 : 0.7 - absOffset * 0.2,
                            zIndex: isActive ? 100 : 10 - absOffset,
                            pointerEvents: absOffset > 2 ? 'none' : 'auto'
                          }}
                          onClick={() => setSelectedTemplateIndex(index)}
                        >
                          <div className="template-carousel-card">
                            <div className="template-carousel-card-header">
                              <div className="template-carousel-title">{template.title}</div>
                              <span className={`template-badge ${template.is_own ? 'own' : 'public'}`}>
                                {template.is_own ? 'Mein' : 'Öffentlich'}
                              </span>
                            </div>
                            <div className="template-carousel-meta">
                              <span className="template-carousel-creator">
                                {formatTemplateCreator(template)}
                              </span>
                              <span className="template-carousel-usage">
                                {template.usage_count || 0}x genutzt
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Navigation Arrows */}
                  <button
                    className="template-carousel-nav prev"
                    onClick={() => setSelectedTemplateIndex(prev =>
                      prev <= 0 ? filteredTemplates.length - 1 : prev - 1
                    )}
                    disabled={filteredTemplates.length <= 1}
                  >
                    ‹
                  </button>
                  <button
                    className="template-carousel-nav next"
                    onClick={() => setSelectedTemplateIndex(prev =>
                      prev >= filteredTemplates.length - 1 ? 0 : prev + 1
                    )}
                    disabled={filteredTemplates.length <= 1}
                  >
                    ›
                  </button>
                </div>

                {/* Selected Template Preview */}
                {filteredTemplates[selectedTemplateIndex] && (
                  <div className="template-carousel-preview">
                    <div className="template-preview-prompt">
                      {filteredTemplates[selectedTemplateIndex].prompt || 'Kein Prompt-Text vorhanden'}
                    </div>
                    <button
                      className="template-carousel-select-btn"
                      onClick={() => handleTemplateSelect(filteredTemplates[selectedTemplateIndex])}
                    >
                      Auswählen
                    </button>
                  </div>
                )}

                {/* Dot Indicators - nur wenn weniger als 10 Templates */}
                {filteredTemplates.length <= 10 && (
                  <div className="template-carousel-dots">
                    {filteredTemplates.map((_, index) => (
                      <button
                        key={index}
                        className={`template-dot ${index === selectedTemplateIndex ? 'active' : ''}`}
                        onClick={() => setSelectedTemplateIndex(index)}
                      />
                    ))}
                  </div>
                )}

                {/* Counter wenn mehr als 10 Templates */}
                {filteredTemplates.length > 10 && (
                  <div className="template-carousel-counter">
                    {selectedTemplateIndex + 1} / {filteredTemplates.length}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};
